import * as THREE from "three";

/**
 * Procedural loot meshes. One builder per catalogue type; every builder takes
 * the *rotated* footprint (w along x, d along z) in deck cells and returns a
 * group whose origin sits at the footprint's minimum corner, resting on y=0.
 *
 * The shapes carry the rules: stackable loot is built with a flat, full-area
 * top; non-stackable loot visibly can't carry anything (domed lid, fuse,
 * cage ring, cannon barrel, idol head).
 */

export const CELL = 1;          // deck cell, world units
export const LAYER_H = 0.62;    // one cargo layer, world units

const mat = (color: number, opts: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.82, metalness: 0.06, ...opts });

/* one shared set of materials (disposed never — tiny) */
const M = {
  pale: mat(0xc8a36a),         // pale crate wood
  paleDark: mat(0xa07d49),
  wood: mat(0x8a5d33),         // barrel wood
  woodDark: mat(0x6b4526),
  tar: mat(0x3a2d22),          // tarred keg
  iron: mat(0x4c4a45, { metalness: 0.55, roughness: 0.45 }),
  brass: mat(0xcf9f4e, { metalness: 0.75, roughness: 0.32 }),
  gold: mat(0xe9b958, { metalness: 0.85, roughness: 0.28 }),
  goldDeep: mat(0xbd8c33, { metalness: 0.8, roughness: 0.3 }),
  silk: mat(0x8e3b54, { roughness: 0.65 }),
  silkRope: mat(0xd8c08c),
  red: mat(0x9c3327),
  green: mat(0x4d8a4a, { roughness: 0.6 }),
  white: mat(0xe8e0cc, { roughness: 0.7 }),
};

function box(w: number, h: number, d: number, m: THREE.Material, x = 0, y = 0, z = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  return mesh;
}
function cyl(rTop: number, rBot: number, h: number, m: THREE.Material, x = 0, y = 0, z = 0, seg = 14): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, seg), m);
  mesh.position.set(x, y, z);
  return mesh;
}

/* Each builder works in a local frame centered on the footprint, floor y=0. */
type Builder = (w: number, d: number) => THREE.Group;

const sugar: Builder = (w, d) => {
  const g = new THREE.Group();
  const s = 0.92;
  g.add(box(w * s, LAYER_H, d * s, M.pale, 0, LAYER_H / 2, 0));
  // rope cross over the lid (flat enough to stack on)
  g.add(box(w * s + 0.02, 0.025, 0.1, M.paleDark, 0, LAYER_H + 0.012, 0));
  g.add(box(0.1, 0.025, d * s + 0.02, M.paleDark, 0, LAYER_H + 0.012, 0));
  return g;
};

const rum: Builder = () => {
  const g = new THREE.Group();
  const r = 0.4;
  g.add(cyl(r * 0.92, r * 0.92, LAYER_H, M.wood, 0, LAYER_H / 2, 0));
  const belly = cyl(r, r, LAYER_H * 0.5, M.wood, 0, LAYER_H / 2, 0);
  g.add(belly);
  for (const y of [LAYER_H * 0.18, LAYER_H * 0.82]) g.add(cyl(r * 0.95, r * 0.95, 0.045, M.iron, 0, y, 0));
  g.add(cyl(r * 0.93, r * 0.93, 0.02, M.woodDark, 0, LAYER_H, 0)); // flat lid
  return g;
};

const powder: Builder = () => {
  const g = new THREE.Group();
  const r = 0.38;
  g.add(cyl(r, r, LAYER_H * 0.92, M.tar, 0, LAYER_H * 0.46, 0));
  g.add(cyl(r + 0.015, r + 0.015, 0.05, M.red, 0, LAYER_H * 0.5, 0)); // hazard band
  // the fuse: a knob and a curl — nothing rests on this
  g.add(cyl(0.09, 0.12, 0.1, M.woodDark, 0, LAYER_H * 0.92 + 0.05, 0));
  const fuse = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.022, 6, 12, Math.PI * 1.4), M.silkRope);
  fuse.position.set(0.02, LAYER_H * 0.92 + 0.16, 0);
  fuse.rotation.z = 0.7;
  g.add(fuse);
  return g;
};

const cage: Builder = () => {
  const g = new THREE.Group();
  const r = 0.34;
  g.add(cyl(r + 0.04, r + 0.04, 0.06, M.brass, 0, 0.03, 0));
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    g.add(cyl(0.016, 0.016, LAYER_H * 0.72, M.brass, Math.cos(a) * r, 0.06 + LAYER_H * 0.36, Math.sin(a) * r));
  }
  const dome = new THREE.Mesh(new THREE.SphereGeometry(r + 0.02, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), M.brass);
  dome.position.y = 0.06 + LAYER_H * 0.72;
  g.add(dome);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.02, 6, 12), M.brass);
  ring.position.y = 0.06 + LAYER_H * 0.72 + (r + 0.02) * 0.55 + 0.08;
  g.add(ring);
  // the parrot: a proud green blob on a perch
  g.add(box(0.3, 0.025, 0.05, M.woodDark, 0, 0.3, 0));
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), M.green);
  body.scale.y = 1.35;
  body.position.set(0.05, 0.41, 0);
  g.add(body);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.07, 8), M.gold);
  beak.rotation.z = -Math.PI / 2;
  beak.position.set(0.16, 0.46, 0);
  g.add(beak);
  return g;
};

const muskets: Builder = (w, d) => {
  const g = new THREE.Group();
  const long = Math.max(w, d), across = Math.min(w, d);
  const horizontalX = w >= d;
  const lid = new THREE.Group();
  lid.add(box(long * 0.94, LAYER_H * 0.8, across * 0.86, M.paleDark, 0, LAYER_H * 0.4, 0));
  lid.add(box(long * 0.98, LAYER_H * 0.18, across * 0.9, M.pale, 0, LAYER_H * 0.86, 0));
  for (const t of [-0.3, 0.3]) lid.add(box(0.07, LAYER_H * 0.97, across * 0.92, M.iron, t * long, LAYER_H * 0.48, 0));
  if (!horizontalX) lid.rotation.y = Math.PI / 2;
  g.add(lid);
  return g;
};

const chest: Builder = (w, d) => {
  const g = new THREE.Group();
  const long = Math.max(w, d), across = Math.min(w, d);
  const c = new THREE.Group();
  c.add(box(long * 0.9, LAYER_H * 0.55, across * 0.82, M.woodDark, 0, LAYER_H * 0.275, 0));
  // the domed lid — nothing may rest on it
  const lid = new THREE.Mesh(
    new THREE.CylinderGeometry(across * 0.41, across * 0.41, long * 0.9, 14, 1, false, 0, Math.PI),
    M.wood);
  lid.rotation.z = Math.PI / 2;
  lid.position.y = LAYER_H * 0.55;
  c.add(lid);
  for (const t of [-0.32, 0, 0.32]) c.add(box(0.06, LAYER_H * 0.6, across * 0.85, M.gold, t * long, LAYER_H * 0.3, 0));
  const clasp = box(0.1, 0.12, 0.05, M.gold, 0, LAYER_H * 0.5, across * 0.42);
  c.add(clasp);
  if (w < d) c.rotation.y = Math.PI / 2;
  g.add(c);
  return g;
};

const planks: Builder = (w, d) => {
  const g = new THREE.Group();
  const long = Math.max(w, d);
  const p = new THREE.Group();
  for (let i = 0; i < 3; i++) {
    const lay = box(long * 0.96, LAYER_H * 0.3, 0.27, i % 2 ? M.paleDark : M.wood,
      0, LAYER_H * (0.15 + 0.3 * i), (i % 2 ? -1 : 1) * 0.04);
    p.add(lay);
    const lay2 = box(long * 0.96, LAYER_H * 0.3, 0.27, i % 2 ? M.wood : M.paleDark,
      0, LAYER_H * (0.15 + 0.3 * i), (i % 2 ? 1 : -1) * 0.26);
    p.add(lay2);
  }
  p.add(box(0.08, LAYER_H * 0.92, 0.62, M.silkRope, -long * 0.3, LAYER_H * 0.46, 0));
  p.add(box(0.08, LAYER_H * 0.92, 0.62, M.silkRope, long * 0.3, LAYER_H * 0.46, 0));
  if (w < d) p.rotation.y = Math.PI / 2;
  g.add(p);
  return g;
};

const silk: Builder = (w, d) => {
  const g = new THREE.Group();
  const bale = new THREE.Mesh(new THREE.BoxGeometry(w * 0.92, LAYER_H * 0.94, d * 0.92, 2, 2, 2), M.silk);
  bale.position.y = LAYER_H * 0.48;
  // plump the bale a little
  const pos = bale.geometry.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const k = 1 + 0.07 * (1 - Math.abs(y) / (LAYER_H * 0.47));
    pos.setX(i, x * k); pos.setZ(i, z * k);
  }
  bale.geometry.computeVertexNormals();
  g.add(bale);
  g.add(box(w * 0.94, 0.03, 0.1, M.silkRope, 0, LAYER_H * 0.945, d * 0.18));
  g.add(box(w * 0.94, 0.03, 0.1, M.silkRope, 0, LAYER_H * 0.945, -d * 0.18));
  g.add(box(0.1, 0.03, d * 0.94, M.silkRope, w * 0.18, LAYER_H * 0.945, 0));
  return g;
};

const cannon: Builder = (w, d) => {
  const g = new THREE.Group();
  const long = Math.max(w, d);
  const c = new THREE.Group();
  // carriage
  c.add(box(long * 0.6, LAYER_H * 0.3, 0.5, M.woodDark, 0, LAYER_H * 0.24, 0));
  for (const sx of [-0.18, 0.18]) for (const sz of [-0.3, 0.3]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.07, 10), M.wood);
    wheel.rotation.x = Math.PI / 2;
    wheel.position.set(sx * long, 0.13, sz);
    c.add(wheel);
  }
  // the barrel — round: nothing rests on a cannon
  const barrel = cyl(0.16, 0.22, long * 0.92, M.brass, 0, LAYER_H * 0.52, 0, 14);
  barrel.rotation.z = Math.PI / 2;
  c.add(barrel);
  c.add(cyl(0.23, 0.23, 0.1, M.brass, -long * 0.18, LAYER_H * 0.52, 0));
  const muzzle = cyl(0.18, 0.16, 0.08, M.goldDeep, long * 0.44, LAYER_H * 0.52, 0);
  muzzle.rotation.z = Math.PI / 2;
  c.add(muzzle);
  if (w < d) c.rotation.y = Math.PI / 2;
  g.add(c);
  return g;
};

const idol: Builder = () => {
  const g = new THREE.Group();
  const H = LAYER_H * 2;
  g.add(box(0.66, H * 0.18, 0.66, M.goldDeep, 0, H * 0.09, 0));
  g.add(box(0.46, H * 0.42, 0.42, M.gold, 0, H * 0.39, 0));
  // crossed arms
  g.add(box(0.56, H * 0.08, 0.46, M.goldDeep, 0, H * 0.5, 0));
  // the head — crowned, and certainly not a shelf
  g.add(box(0.36, H * 0.26, 0.34, M.gold, 0, H * 0.73, 0));
  const eyeL = box(0.07, 0.05, 0.02, M.red, -0.08, H * 0.76, 0.18);
  const eyeR = box(0.07, 0.05, 0.02, M.red, 0.08, H * 0.76, 0.18);
  g.add(eyeL, eyeR);
  const crown = new THREE.Mesh(new THREE.ConeGeometry(0.22, H * 0.16, 4), M.goldDeep);
  crown.rotation.y = Math.PI / 4;
  crown.position.y = H * 0.9;
  g.add(crown);
  return g;
};

// a tall (2-layer) tea chest: two banded crates with a flat, full-area lid —
// the load-bearing top says "stack on me", unlike the idol's crowned head
const tea: Builder = (w, d) => {
  const g = new THREE.Group();
  const H = LAYER_H * 2;
  const s = 0.9;
  for (const yc of [H * 0.25, H * 0.71]) {
    g.add(box(w * s, H * 0.4, d * s, M.pale, 0, yc, 0));
    for (const band of [yc + H * 0.17, yc - H * 0.17])
      g.add(box(w * s + 0.02, 0.04, d * s + 0.02, M.iron, 0, band, 0));
  }
  for (const sx of [-1, 1]) for (const sz of [-1, 1])
    g.add(box(0.06, H * 0.94, 0.06, M.paleDark, sx * w * s * 0.5, H * 0.47, sz * d * s * 0.5));
  g.add(box(w * s, 0.06, d * s, M.paleDark, 0, H * 0.95, 0)); // flat lid — stackable
  return g;
};

const BUILDERS: Record<string, Builder> = {
  sugar, rum, powder, cage, muskets, chest, planks, silk, cannon, idol, tea,
};

/**
 * Build the mesh for a loot type at a rotated footprint (w×d cells, h layers).
 * Origin: center of the footprint, base at y=0 — position the group at the
 * footprint center, deck height of the resting layer.
 */
export function buildLootMesh(key: string, w: number, d: number): THREE.Group {
  const g = (BUILDERS[key] ?? sugar)(w, d);
  g.traverse((o) => {
    if (o instanceof THREE.Mesh) { o.castShadow = true; o.receiveShadow = true; }
  });
  return g;
}

/** A translucent copy used as the placement ghost (green = legal, red = not). */
export function buildGhostMesh(key: string, w: number, d: number): { group: THREE.Group; setValid: (ok: boolean) => void } {
  const g = (BUILDERS[key] ?? sugar)(w, d);
  const matOk = new THREE.MeshBasicMaterial({ color: 0x7fe08a, transparent: true, opacity: 0.5, depthWrite: false });
  const matBad = new THREE.MeshBasicMaterial({ color: 0xe0644d, transparent: true, opacity: 0.5, depthWrite: false });
  const meshes: THREE.Mesh[] = [];
  g.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.material = matOk;
      o.raycast = () => undefined; // the ghost is visual only — rays pass through
      meshes.push(o);
    }
  });
  return {
    group: g,
    setValid: (ok: boolean) => { for (const m of meshes) m.material = ok ? matOk : matBad; },
  };
}
